// Minimize UI wrapper usage to avoid extra dependencies

import {
  AdminRoute,
  AuthCallback,
  AuthenticatedRootGateway,
  AuthenticatedRoute,
  AuthProvider,
  ConnectedAppAuthorize,
  DiscoveryCallback,
  NoAccessPage,
  NotFoundPage,
  ProtectedRoute,
  StytchProvider,
} from '@platform/auth';
import { adminAccessPermissions } from '@platform/auth/permissions';
import { MobileWarning } from '@shared/ui';
import { Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import AdminHomePage from './features/admin/AdminHomePage';
import AdminUsersPage from './features/admin/AdminUsersPage';
import ArtifactsDashboardPage from './features/admin/ArtifactsDashboardPage';
import CoachDetailPage from './features/admin/CoachDetailPage';
import CoachesListPage from './features/admin/CoachesListPage';
import CoachMemberDetailPage from './features/admin/CoachMemberDetailPage';
import CronJobsPage from './features/admin/CronJobsPage';
import EmailPreviewPage from './features/admin/EmailPreviewPage';
import EvalRunDetailPage from './features/admin/EvalRunDetailPage';
import EvalsDashboard from './features/admin/EvalsDashboard';
import { FeatureFlagsPage } from './features/admin/FeatureFlagsPage';
import InternalAppsPage from './features/admin/InternalAppsPage';
import MemberDetailPage from './features/admin/MemberDetailPage';
import MemberReflectionBackfillPage from './features/admin/MemberReflectionBackfillPage';
import MembersListPage from './features/admin/MembersListPage';
import SentEmailsPage from './features/admin/SentEmailsPage';
import TranscriptCoveragePage from './features/admin/TranscriptCoveragePage';
import { TypeformFormMappingsPage } from './features/admin/TypeformFormMappingsPage';
import { TypeformPage } from './features/admin/TypeformPage';
import { TypeformSubmissionDetailPage } from './features/admin/TypeformSubmissionDetailPage';
import AdminAppointmentDetailPage from './features/admin-appointments/AdminAppointmentDetailPage';
import AdminAppointmentsPage from './features/admin-appointments/AdminAppointmentsPage';
import AdminBookSeriesPage from './features/admin-book-series/AdminBookSeriesPage';
import { AdminCalendarChangeDetail } from './features/admin-calendar-changes/AdminCalendarChangeDetail';
import { AdminCalendarChangesPage } from './features/admin-calendar-changes/AdminCalendarChangesPage';
import AdminCoachCalendarRoute from './features/admin-calendar-view/AdminCoachCalendarRoute';
import AgentsTestPage from './features/agents/AgentsTestPage';
import CalendarSettingsRoute from './features/calendar-settings/CalendarSettingsRoute';
import ChatPage from './features/chat/ChatPage';
import ChatPageWithSidebar from './features/chat/ChatPageWithSidebar';
import ArtifactGenerationPage from './features/coach-artifacts/ArtifactGenerationPage';
import TranscriptArtifactDetailPage from './features/coach-artifacts/TranscriptArtifactDetailPage';
import CoachMatchingPage from './features/coach-matching/CoachMatchingPage';
import SummaryPage from './features/drive1on1/SummaryPage';
import DriveConnectComplete from './features/integrations/drive/pages/DriveConnectComplete';
import IntegrationsOverviewPage from './features/integrations/IntegrationsOverviewPage';
import SettingsRouter from './features/integrations/SettingsRouter';
import LoginPage from './features/login/LoginPage';
import ManualTranscriptsPage from './features/manual-transcripts/ManualTranscriptsPage';
import {
  CoachSessionArtifactsPage,
  CoachSessionTranscriptPage,
  MemberArtifactsHomePage,
  MemberListPage,
  MembersByEmailRedirectPage,
} from './features/members';
import ProfileDetailPage from './features/profile-editor/ProfileDetailPage';
import ProfileListPage from './features/profile-editor/ProfileListPage';
import PromptEditorPage from './features/prompts/PromptEditorPage';
import PromptTesterPage from './features/prompts/PromptTesterPage';
import ProtectedLayoutRoute from './layout/ProtectedLayoutRoute';
import { useMixpanelInit } from './platform/analytics/useMixpanelInit';
import { usePageViewTracking } from './platform/analytics/usePageViewTracking';
import { useIsSmallScreen } from './shared/hooks/useIsSmallScreen';

const adminCoachPermissions = ['admin:coaches'];

function isStandaloneBookingRoute(pathname: string): boolean {
  return /^\/admin\/coaches\/[^/]+\/book-series\/?$/.test(pathname);
}

function AdminBookSeriesRoute() {
  const { coachId } = useParams<{ coachId: string }>();
  const [searchParams] = useSearchParams();
  const memberId = searchParams.get('memberId') ?? '';
  if (!coachId || !memberId) {
    return <Navigate to="/admin/coaches" replace />;
  }
  return <AdminBookSeriesPage coachId={coachId} memberId={memberId} />;
}

function AdminAppointmentDetailRoute() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  if (!appointmentId) {
    return <Navigate to="/admin/appointments" replace />;
  }
  return <AdminAppointmentDetailPage appointmentId={appointmentId} />;
}

function AdminCalendarChangeDetailRoute() {
  const { changeId } = useParams<{ changeId: string }>();
  if (!changeId) {
    return <Navigate to="/admin/calendar-changes" replace />;
  }
  return <AdminCalendarChangeDetail changeId={changeId} />;
}

function App() {
  useMixpanelInit();
  usePageViewTracking();
  const isSmallScreen = useIsSmallScreen();
  const location = useLocation();

  // Block the app on small screens (phones, tablets, or narrow browser windows)
  if (isSmallScreen && !isStandaloneBookingRoute(location.pathname)) {
    return <MobileWarning />;
  }

  return (
    <StytchProvider>
      <AuthProvider>
        <Routes>
          {/* Public routes (no side nav) */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AuthenticatedRootGateway />
              </ProtectedRoute>
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/discovery-callback" element={<DiscoveryCallback />} />
          <Route path="/oauth/authorize" element={<ConnectedAppAuthorize />} />
          <Route path="/connect-drive/complete" element={<DriveConnectComplete />} />
          <Route
            path="/no-access"
            element={
              <ProtectedRoute>
                <NoAccessPage />
              </ProtectedRoute>
            }
          />

          {/* Protected routes with layout */}
          {/* Member-centric flow - main landing */}
          <Route
            path="/artifacts"
            element={
              <ProtectedLayoutRoute>
                <MemberArtifactsHomePage />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/members_by_email/:email"
            element={
              <ProtectedLayoutRoute>
                <MembersByEmailRedirectPage />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/members"
            element={
              <ProtectedLayoutRoute>
                <MemberListPage />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/members/:personId"
            element={
              <ProtectedLayoutRoute fullWidth>
                <CoachSessionArtifactsPage />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/members/:personId/sessions/:sessionId/transcript"
            element={
              <ProtectedLayoutRoute>
                <CoachSessionTranscriptPage />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/members/:personId/generating"
            element={
              <ProtectedRoute>
                <ArtifactGenerationPage />
              </ProtectedRoute>
            }
          />
          {/* Legacy dashboard - redirect to members */}
          <Route path="/dashboard" element={<Navigate to="/members" replace />} />
          <Route path="/drive1on1" element={<Navigate to="/members" replace />} />
          <Route
            path="/agents"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AgentsTestPage />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedLayoutRoute>
                <ChatPage />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/chat/sidebar"
            element={
              <ProtectedRoute>
                <ChatPageWithSidebar />
              </ProtectedRoute>
            }
          />
          {/* Legacy team routes - redirect to new /members */}
          <Route
            path="/manual-transcripts"
            element={
              <ProtectedLayoutRoute>
                <ManualTranscriptsPage />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/drive1on1/summary"
            element={
              <ProtectedRoute>
                <SummaryPage />
              </ProtectedRoute>
            }
          />
          {/* Direct artifact access for email notification links */}
          <Route
            path="/artifacts/generating"
            element={
              <ProtectedRoute>
                <ArtifactGenerationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artifacts/:artifactId"
            element={
              <ProtectedRoute>
                <TranscriptArtifactDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/integrations"
            element={
              <ProtectedLayoutRoute>
                <IntegrationsOverviewPage />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/settings/calendars"
            element={
              <ProtectedLayoutRoute>
                <CalendarSettingsRoute />
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/settings/integrations/:provider"
            element={
              <ProtectedLayoutRoute>
                <SettingsRouter />
              </ProtectedLayoutRoute>
            }
          />
          <Route path="/settings" element={<Navigate to="/settings/integrations" replace />} />
          <Route path="/connections" element={<Navigate to="/settings/integrations" replace />} />
          {/* Admin routes - more specific routes first */}
          <Route
            path="/admin/prompts"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={['admin:prompts']}>
                  <PromptEditorPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/prompt-tester"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={['admin:prompts']}>
                  <PromptTesterPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/profiles/:slug"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={['admin:profiles']}>
                  <ProfileDetailPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/profiles"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={['admin:profiles']}>
                  <ProfileListPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={['admin:users']}>
                  <AdminUsersPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/calendar-changes/:changeId"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <AdminCalendarChangeDetailRoute />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/calendar-changes"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <AdminCalendarChangesPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/appointments/review"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <AdminAppointmentsPage reviewOnly />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/appointments/:appointmentId"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <AdminAppointmentDetailRoute />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/appointments"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <AdminAppointmentsPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/coaches"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <CoachesListPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/members"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <MembersListPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/members/:memberId"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <MemberDetailPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/coaches/:coachId/calendar"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <AdminCoachCalendarRoute />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/coaches/:coachId/book-series"
            element={
              <ProtectedRoute>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <AdminBookSeriesRoute />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/coaches/:coachId"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <CoachDetailPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/coaches/:coachId/members/:memberId"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <CoachMemberDetailPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/emails"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <SentEmailsPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/email-previews"
            element={
              <ProtectedLayoutRoute
                contentBackground="#F6F8F6"
                fillHeight
                fullWidth
                hideBreadcrumbs
              >
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <EmailPreviewPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/cron"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={['admin:cron']}>
                  <CronJobsPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/typeform/forms/:formId/mappings"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={['admin:onboarding']}>
                  <TypeformFormMappingsPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/typeform/:id"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={['admin:onboarding']}>
                  <TypeformSubmissionDetailPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/typeform"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={['admin:onboarding']}>
                  <TypeformPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/coach-matching"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <AdminRoute requiredPermissions={['admin:prompts']}>
                    <CoachMatchingPage />
                  </AdminRoute>
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/evals"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={['admin:evals']}>
                  <EvalsDashboard />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/evals/runs/:runId"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={['admin:evals']}>
                  <EvalRunDetailPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/internal-apps"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={['admin:internal-apps']}>
                  <InternalAppsPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/feature-flags"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={['admin:*']}>
                  <FeatureFlagsPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/member-reflection-backfill"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={['admin:sessions']}>
                  <MemberReflectionBackfillPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/transcript-coverage"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <TranscriptCoveragePage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin/artifacts"
            element={
              <ProtectedLayoutRoute fullWidth>
                <AdminRoute requiredPermissions={adminCoachPermissions}>
                  <ArtifactsDashboardPage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedLayoutRoute>
                <AdminRoute requiredPermissions={adminAccessPermissions}>
                  <AdminHomePage />
                </AdminRoute>
              </ProtectedLayoutRoute>
            }
          />
          <Route
            path="*"
            element={
              <AuthenticatedRoute>
                <NotFoundPage />
              </AuthenticatedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </StytchProvider>
  );
}

export default App;
